import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Mail, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      // Backend always returns 200; only a network error could land here
      toast.error(err.response?.data?.detail || 'Could not submit request. Try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-[#4B5563] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>
        <Card className="border-[#E5E7EB]" data-testid="forgot-password-card">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#002FA7]" />
              Reset your password
            </CardTitle>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-4" data-testid="forgot-success-view">
                <div className="w-14 h-14 rounded-full bg-[#34C759]/15 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-[#34C759]" />
                </div>
                <p className="text-[#0A0A0A] font-medium mb-2">Check your email</p>
                <p className="text-sm text-[#4B5563] mb-6">
                  If <strong>{email}</strong> matches a KStreak account, a password reset link is on its way.
                  The link expires in 60 minutes. Check your <strong>spam folder</strong> too, just in case.
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  data-testid="back-to-login-btn"
                  className="w-full"
                >
                  Back to login
                </Button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <p className="text-sm text-[#4B5563] mb-4">
                  Enter the email address tied to your account. We'll send you a link to set a new password.
                </p>
                <Label className="text-xs">Email address</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  required
                  className="mt-1 mb-4"
                  data-testid="forgot-email-input"
                />
                <Button
                  type="submit"
                  className="w-full bg-[#002FA7] hover:bg-[#00227A]"
                  disabled={submitting || !email.trim()}
                  data-testid="forgot-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Send reset link
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
